import { useCallback, useEffect, useRef, useState } from 'react';
import HeroShowcase from './HeroShowcase';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import { slideAfterDrag } from '../../utils/heroDrag';
import './HeroSlider.css';

const AUTO_ADVANCE_MS = 6000;

// Slop below which the gesture is still a tap, not a swipe.
const TAP_SLOP = 6;
// The click a swipe produces arrives right after the release, so a short
// window catches it without ever reaching a later, genuine click.
const CLICK_GUARD_MS = 300;

/**
 * The home page hero, as a slider over whatever the store has published -
 * scheduled campaigns, hand-picked products, or the single hero banner.
 *
 * A single slide renders exactly like a static hero: no dots, no timer.
 */
const HeroSlider = ({ slides = [] }) => {
  const count = slides.length;
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  // The gesture lives in a ref because the pointer handlers act on it
  // imperatively; the offset is mirrored into state purely to drive the track.
  const dragRef = useRef(null); // { startX, dx, isMouse } while a drag is live
  const [dragDx, setDragDx] = useState(null);
  const clickGuardRef = useRef(false);
  const clickGuardTimerRef = useRef(null);

  const isDragging = dragDx !== null;

  const goTo = useCallback(
    next => setIndex(((next % count) + count) % count),
    [count]
  );

  const shouldAutoAdvance =
    count > 1 && !isPaused && !prefersReducedMotion && !isDragging;

  useEffect(() => {
    if (!shouldAutoAdvance) return undefined;
    const timer = setInterval(
      () => setIndex(current => (current + 1) % count),
      AUTO_ADVANCE_MS
    );
    return () => clearInterval(timer);
  }, [shouldAutoAdvance, count]);

  // A slide removed by an expiring campaign must not strand the index.
  useEffect(() => {
    if (index > count - 1) setIndex(0);
  }, [index, count]);

  const armClickGuard = useCallback(() => {
    clickGuardRef.current = true;
    window.clearTimeout(clickGuardTimerRef.current);

    // Bounded, so a swipe whose click never lands - the release went outside
    // the hero, or outside the window - cannot leave a trap set for a later,
    // genuine click, a keyboard Enter on a slide's button included.
    clickGuardTimerRef.current = window.setTimeout(() => {
      clickGuardRef.current = false;
    }, CLICK_GUARD_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(clickGuardTimerRef.current), []);

  const finishDrag = useCallback(() => {
    const gesture = dragRef.current;
    dragRef.current = null;
    setDragDx(null);
    if (!gesture) return;

    const dx = gesture.dx || 0;

    // The slides carry their own buttons, so a swipe that ends on one must not
    // also follow it. A gesture that never left the tap slop was a click.
    if (Math.abs(dx) > TAP_SLOP) armClickGuard();

    setIndex(current => slideAfterDrag({ index: current, dx, count }));
  }, [armClickGuard, count]);

  const handlePointerDown = event => {
    if (count < 2) return;
    // Touch and pen swipe; a mouse drags, but only on its primary button.
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    dragRef.current = {
      startX: event.clientX,
      dx: 0,
      isMouse: event.pointerType === 'mouse',
    };
    setDragDx(0);
  };

  // A slide is artwork wrapping buttons, so both are natively draggable. Left
  // alone the browser starts its own drag-and-drop and fires pointercancel,
  // which ends the gesture before it moves a pixel.
  const handleMouseDown = event => {
    if (!dragRef.current || !dragRef.current.isMouse) return;

    event.preventDefault();
    // preventDefault suppresses focus too, and the hero is arrow-key
    // navigable, so the focus a click would have given it is put back by hand.
    event.currentTarget.focus();
  };

  // Tracked on the window so a release outside the hero still ends the
  // gesture. Bound to the element, a mouse let go anywhere else left the track
  // stranded mid-drag and auto-advance paused for good.
  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMove = event => {
      const gesture = dragRef.current;
      if (!gesture) return;

      // The button came up somewhere this page never saw: off-window, or over
      // browser chrome. Nothing else would ever end the gesture.
      if (gesture.isMouse && event.buttons === 0) {
        finishDrag();
        return;
      }

      gesture.dx = event.clientX - gesture.startX;
      setDragDx(gesture.dx);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    window.addEventListener('blur', finishDrag);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
      window.removeEventListener('blur', finishDrag);
    };
  }, [isDragging, finishDrag]);

  const handleClickCapture = event => {
    if (!clickGuardRef.current) return;

    // The swipe's own click has arrived; disarm at once so a genuine click a
    // moment later still lands.
    clickGuardRef.current = false;
    window.clearTimeout(clickGuardTimerRef.current);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleKeyDown = event => {
    if (count < 2) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
  };

  if (!count) return null;

  return (
    <div
      className={`hero-slider${count > 1 ? ' is-draggable' : ''}${
        isDragging ? ' is-grabbing' : ''
      }`}
      role={count > 1 ? 'region' : undefined}
      aria-roledescription={count > 1 ? 'carousel' : undefined}
      aria-label={count > 1 ? 'Featured' : undefined}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onMouseDown={handleMouseDown}
      onClickCapture={handleClickCapture}
      onDragStart={event => event.preventDefault()}
      tabIndex={count > 1 ? 0 : undefined}
    >
      <div
        className={`hero-slider-track${isDragging ? ' is-dragging' : ''}`}
        style={{
          transform: `translateX(calc(-${index * 100}% + ${dragDx || 0}px))`,
        }}
      >
        {slides.map((slide, slideIndex) => {
          const isCurrent = slideIndex === index;

          return (
            <div
              key={slide.id}
              className="hero-slider-slide"
              /* `inert` keeps the buttons on an off-screen slide out of both
                 the tab order and the accessibility tree. */
              inert={!isCurrent}
              aria-hidden={!isCurrent}
            >
              {/* Only the first slide is the page's h1 - the rest are
                  peers, not additional top-level headings. */}
              <HeroShowcase
                kicker={slide.kicker}
                heading={slide.heading}
                headingAs={slideIndex === 0 ? 'h1' : 'h2'}
                subtitle={slide.subtitle}
                image={slide.image}
                mobileImage={slide.mobileImage}
                primary={slide.primary}
                secondary={slide.secondary}
                eager={slideIndex === 0}
              />
            </div>
          );
        })}
      </div>

      {count > 1 && (
        <div className="hero-slider-dots">
          {slides.map((slide, dotIndex) => (
            <button
              key={slide.id}
              type="button"
              className={`hero-slider-dot${dotIndex === index ? ' is-active' : ''}`}
              aria-label={`Show ${slide.heading}`}
              aria-current={dotIndex === index}
              onClick={() => goTo(dotIndex)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroSlider;
